import CircularProgress from "@material-ui/core/CircularProgress";
import addMonths from "date-fns/addMonths";
import Joi from "joi";
import * as PropTypes from "prop-types";
import React, {useCallback, useEffect, useState} from "react";
import {toast} from "react-toastify";
import {SYSTEMADMIN} from "../../consts/funktionaleRollen";
import {
    funktionaleRollenAsObjects,
    getFunktionaleRollenByGlobaleRolle, GLOBALE_ROLLE_ADMIN,
    GLOBALE_ROLLE_BEHOERDE,
    GLOBALE_ROLLE_ORGANISATION,
    globaleRollen,
} from "../../consts/valueLists";
import {getAnwenderListEndpoint, postAnwenderExportEndpointConfiguration} from "../../services/AnwenderService";
import {getAnredeEndpoint, getTitelEndpoint} from "../../services/WertelistenService";
import http, {handleApiException, handleValidatedApiException, isApiCancel, useHttp} from "../../services/HttpService";
import {logger} from "../../services/LogService";
import {getOrganisationenRegisterEndpoint} from "../../services/OrganisationsService";
import {getLokaleRollenEndpoint} from "../../services/RollenService";
import "../../styles/components/anwenderListComponent.css";
import {createCalculatedColumn, createColumn, createComponentColumn, createSelectRowComponentColumn} from "../../utils/ColumnUtils";
import {useForm} from "../../utils/FormUtils";
import {userHasAnyRole} from "../../utils/SecurityUtils";
import {useHttpSortById, usePreformattedHttp} from "../../utils/usePreformattedHttp";
import {requiredEnumWerteListeObjekt, requiredNumber, requiredObject, requiredString} from "../../utils/Validator";
import PersonalDataComponent from "../data/user/PersonalDataComponent";
import EAButton from "../EAButton";
import EAButtonBar from "../EAButtonBar";
import EADialog from "../EADialog";
import EASelectButton from "../EASelectButton";
import EATable from "../EATable";
import EATableActionButton from "../tableComponents/EATableActionButton";
import AnwenderListFilter from "./AnwenderListFilter";
import EADownloadButton from "../EADownloadButton";
import {changeFieldValueIfPresent} from "../../utils/ObjectUtils";
import {useConfirmation} from "../ConfirmationDialog/ConfirmationDialogProvider";
import {confirmDeleteOption} from "../ConfirmationDialog/confirmationDialogOptions";
import {createPersonSchema} from "../../initialObjects/AnwenderObjects";

function TableDialog({open, data, onSaveSuccessful, onClickCancel, titelOptionen, anredeOptionen, lokaleRollenOptionen, orgaOptionen}) {
    const {values, setValues, formFactory, errors, submit, submitLoading} = useForm({
        initialValues: data,
        schema: {
            person: {
                ...createPersonSchema(),
                organisation: data?.globaleRolle?.id === GLOBALE_ROLLE_ORGANISATION ? requiredObject({id: requiredNumber()}) : Joi.any(),
            },
            benutzername: requiredString(),
            passwort: data?.id ? Joi.any() : requiredString(),
            datascoutId: data?.globaleRolle?.id !== GLOBALE_ROLLE_ORGANISATION ? requiredNumber() : Joi.any(),
            globaleRolle: requiredEnumWerteListeObjekt(),
            funktionaleRolle: Joi.array()
                .items(requiredEnumWerteListeObjekt())
                .error((tmpErrors) => {
                    tmpErrors[0].message = "Es muss mindestens ein Element ausgewählt werden.";
                    return tmpErrors[0];
                }),
            lokaleRollen:
                data?.globaleRolle?.id === GLOBALE_ROLLE_BEHOERDE
                    ? Joi.array()
                        .items(
                            Joi.object({
                                systemrolle: Joi.boolean().invalid(false).required(),
                            }).required(),
                            Joi.any()
                        )
                        .sparse()
                        .error((errs) => {
                            errs[0].message =
                                "Ein Behördenanwender benötigt mindestens eine lokale Rolle, welche die Dienststelle repräsentiert.";
                            return errs[0];
                        })
                    : Joi.array(),
        },
    });
    const [{data: anwender, loading}] = useHttp("/anwender/" + data.id, {manual: !data?.id});
    useEffect(() => {
        if (anwender) {
            logger.debug("Anwender geladen", anwender);
            anwender.globaleRolle = globaleRollen.find((rolle) => rolle.id === anwender.globaleRolle);
            if (anwender.funktionaleRolle) {
                anwender.funktionaleRolle = anwender.funktionaleRolle.map((rolle) =>
                    funktionaleRollenAsObjects.find((fRolle) => fRolle.id === rolle)
                );
            }
            anwender.status = anwender.status === "AKTIV";
            anwender.passwort = "";
            setValues(anwender);
        }
    }, [anwender, setValues]);

    function handleKontaktChanged(field, value) {
        const valueToSet = {...values};
        valueToSet.person[field] = value;
        setValues(valueToSet);
    }

    const prepareForUpload = useCallback((clonedAnwender) => {
        const id = clonedAnwender.id;
        const dataToSave = {...clonedAnwender, ...clonedAnwender.person};
        dataToSave.id = id;
        if (!dataToSave.id) {
            if (!dataToSave.benutzername) {
                dataToSave.benutzername = dataToSave.person.email;
                setValues({...dataToSave});
            }
        }
        changeFieldValueIfPresent(dataToSave, "globaleRolle", "id");
        dataToSave.funktionaleRolle = dataToSave.funktionaleRolle.map((funktionaleRolle) => funktionaleRolle.id);
        dataToSave.status = dataToSave.status ? "AKTIV" : "INAKTIV";
        changeFieldValueIfPresent(dataToSave, "titel", "id");
        if (dataToSave.anrede) {
            dataToSave.anrede = dataToSave.anrede.id;
        }
        if (dataToSave.organisation) {
            dataToSave.organisation = dataToSave.organisation.id;
        }
        if (dataToSave.lokaleRollen) {
            dataToSave.lokaleRollen = dataToSave.lokaleRollen.map((lokaleRolle) => lokaleRolle.id);
        }
        return dataToSave;
    }, [setValues]);

    function handleSave() {
        logger.debug("Saving data", values);
        submit(prepareForUpload, values.id ? "/anwender/" + values.id : "/anwender/", values.id ? "PUT" : "POST")
            .then(() => {
                onSaveSuccessful();
            })
            .catch((e) => handleValidatedApiException(e, "Error saving Anwender"));
    }

    function filterLokaleRollen(lokaleRollen) {
        if (data.globaleRolle && data.globaleRolle.id !== GLOBALE_ROLLE_ADMIN && lokaleRollen) {
            let filteredRollen = [];
            lokaleRollen.forEach((lokaleRolle) => {
                if(data.globaleRolle.id === lokaleRolle.globaleRolle.id) {
                    filteredRollen.push(lokaleRolle);
                }
            });
            return filteredRollen;
        }
        return lokaleRollen;
    }

    return (
        <EADialog
            open={open}
            titleChildren={"Anwender " + (data.id ? "bearbeiten" : "erstellen")}
            content={
                <div className="gridl" style={{width: "600px"}}>
                    {((submitLoading || loading) && <CircularProgress style={{justifySelf: "center"}}/>) || (
                        <div className="gridl" style={{gridTemplateColumns: "1fr"}}>
                            <PersonalDataComponent
                                person={values.person}
                                anredeOptions={anredeOptionen}
                                titelOptions={titelOptionen}
                                errors={errors}
                                handlePersonChanged={handleKontaktChanged}
                                additionalPrefixChildren={
                                    <React.Fragment>
                                        {formFactory.renderCheckbox("status", "status", "Aktiv", false, {
                                            style: {gridColumnEnd: "span 2"},
                                        })}
                                    </React.Fragment>
                                }
                                additionalChildren={
                                    <React.Fragment>
                                        {formFactory.renderInput("benutzername", "benutzername", "Benutzername", "text", false, {
                                            style: {gridColumn: 1},
                                        })}
                                        {formFactory.renderInput(
                                            "datascoutId",
                                            "datascoutId",
                                            "Datascout Id",
                                            "text",
                                            data?.globaleRolle?.id === GLOBALE_ROLLE_ORGANISATION
                                        )}
                                        {formFactory.renderInput("passwort", "passwort", "Passwort", "password", false, {
                                            InputProps: {autoComplete: "new-password"},
                                        })}
                                        {formFactory.renderDatePicker("angelegtAm", "angelegtAm", "Angelegt am", true, {
                                            style: {gridColumn: 1},
                                        })}
                                        {formFactory.renderDatePicker("gueltigBis", "gueltigBis", "Gültig Bis", false, {})}
                                        {formFactory.renderAutocomplete(
                                            "person.organisation",
                                            "person.organisation",
                                            "Organisation",
                                            orgaOptionen.data,
                                            (orga) => {
                                                return `${orga.bezeichnung} - ${orga.adresse.strasse} ${orga.adresse.hausnummer}, ${orga.adresse.plz} ${orga.adresse.ort}`;
                                            },
                                            "id",
                                            orgaOptionen.loading,
                                            false,
                                            data?.globaleRolle?.id !== GLOBALE_ROLLE_ORGANISATION,
                                            {style: {gridColumnEnd: "span 2"}}
                                        )}
                                        {formFactory.renderSelect("globaleRolle", "globaleRolle", "Rolle", globaleRollen, true, {
                                            style: {gridColumnEnd: "span 2"},
                                        })}
                                        {formFactory.renderAutocompleteWerteliste(
                                            "funktionaleRolle",
                                            "funktionaleRolle",
                                            "Funktionale Rollen",
                                            {data: getFunktionaleRollenByGlobaleRolle(data.globaleRolle.id), loading: false},
                                            true,
                                            false,
                                            {style: {gridColumnEnd: "span 2"}}
                                        )}
                                        {formFactory.renderAutocompleteWithCustomChipLabelFunction(
                                            "lokaleRollen",
                                            "lokaleRollen",
                                            "Lokale Rollen",
                                            filterLokaleRollen(lokaleRollenOptionen.data),
                                            (item) => item.bezeichnung,
                                            "bezeichnung",
                                            "id",
                                            lokaleRollenOptionen.loading,
                                            true,
                                            false,
                                            {style: {gridColumnEnd: "span 2"}}
                                        )}
                                    </React.Fragment>
                                }
                            />
                        </div>
                    )}
                </div>
            }
            buttons={
                !submitLoading && (
                    <EAButtonBar>
                        <EAButton id={"btnAbbrechen"} data-cy={"btnAbbrechen"} label={"Abbrechen"} onClick={onClickCancel}/>
                        <EAButton id={"btnOk"} data-cy={"btnOk"} color="primary" label={"Übernehmen"} onClick={() => handleSave()}/>
                    </EAButtonBar>
                )
            }
        />
    );
}

TableDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    data: PropTypes.any.isRequired,
    onClickCancel: PropTypes.func.isRequired,
};

const initialFilterProps = {
    vorname: "",
    name: "",
    organisation: "",
    statusBeantragt: true,
    statusBestaetigt: true,
    statusAktiv: true,
    statusInaktiv: true,
    rolleOrganisation: true,
    rolleBehoerde: true,
    rolleAdministration: true
};

function AnwenderListComponent() {
    const confirm = useConfirmation();
    const [{data, loading}, refetch] = usePreformattedHttp(
        (dataToMap) =>
            dataToMap.map((item) => {
                item.globaleRolle = globaleRollen.find((rolle) => rolle.id === item.globaleRolle);
                if (item.funktionaleRolle) {
                    item.funktionaleRolle = item.funktionaleRolle.map((rolle) =>
                        funktionaleRollenAsObjects.find((fRolle) => fRolle.id === rolle)
                    );
                }
                item.lokaleRollen = [];
                return item;
            }),
        getAnwenderListEndpoint
    );
    const [titelOptionen] = useHttpSortById(getTitelEndpoint);
    const [anredeOptionen] = useHttpSortById(getAnredeEndpoint);
    const [lokaleRollenOptionen] = useHttp(getLokaleRollenEndpoint);
    const [orgaOptionen] = useHttpSortById(getOrganisationenRegisterEndpoint);
    const [filteredData, setFilteredData] = useState([]);
    const [volatileFilterProps, setVolatileFilterProps] = useState(initialFilterProps);
    const [filterProps, setFilterProps] = useState(initialFilterProps);

    const [selected, setSelected] = useState([]);
    const [dlgOpen, setDlgOpen] = useState(false);
    const [tableIndex, setTableIndex] = useState(-1);
    const [rollenIndex, setRollenIndex] = useState(-1);

    const columns = [
        createSelectRowComponentColumn("anwenderListe", "id", data, selected, setSelected),
        createComponentColumn("actions", (row, index) => {
            if (isDisabled()) return <div/>;
            return (
                <div className="gridl" style={{gridTemplateColumns: "repeat(2,min-content)", marginLeft: "8px"}}>
                    <EATableActionButton id={"editWert-" + index} iconName="ico-edit">
                        <span
                            onClick={() => handleEdit(row.id)}
                            className={"w-100"}
                            style={{
                                height: "100%",
                                display: "block",
                            }}
                        />
                    </EATableActionButton>
                    <EATableActionButton id={"deleteWert-" + index} iconName="ico-delete">
                        <span
                            onClick={() => handleDelete(row.id)}
                            className={"w-100"}
                            style={{
                                height: "100%",
                                display: "block",
                            }}
                        />
                    </EATableActionButton>
                </div>
            );
        }),
        createComponentColumn(
            "status",
            (row) => {
                if (row.status === "AKTIV") {
                    return "aktiv";
                }
                else if (row.status === "BESTAETIGT") {
                    return "bestätigt";
                }
                else if (row.status === "DOUBLE_OPT_IN" || row.status === "DOUBLE_OPT_IN_CHANGED_EMAIL") {
                    return "beantragt";
                }
                else {
                    return "inaktiv";
                }
            },
            "Status"
        ),
        createColumn("person.vorname", "Vorname"),
        createColumn("person.name", "Name"),
        createColumn("person.organisation.bezeichnung", "Organisation"),
        createColumn("globaleRolle.value", "Rolle"),
        createCalculatedColumn(
            "funktion",
            (row) => row?.funktionaleRolle.map((rolle) => (rolle?.value ? rolle?.value : rolle))?.join(", "),
            "Funktion"
        ),
    ];
    const filterData = useCallback(() => {
        setFilteredData([
            ...data.filter((item) => {
                return (
                    (!filterProps.vorname || item.person.vorname.toLowerCase().includes(filterProps.vorname.toLowerCase())) &&
                    (!filterProps.name || item.person.name.toLowerCase().includes(filterProps.name.toLowerCase())) &&
                    (!filterProps.organisation || (item.person.organisation &&
                        item.person.organisation.bezeichnung.toLowerCase().includes(filterProps.organisation.toLowerCase()))) &&
                    (!item.globaleRolle || (filterProps.rolleOrganisation && item.globaleRolle.id === "ORGANISATION") ||
                        (filterProps.rolleBehoerde && item.globaleRolle.id === "BEHOERDE") ||
                        (filterProps.rolleAdministration && item.globaleRolle.id === "ADMIN")) &&
                    ((filterProps.statusAktiv && item.status === "AKTIV") ||
                        (filterProps.statusInaktiv && item.status === "INAKTIV") ||
                        (filterProps.statusBeantragt && (item.status === "DOUBLE_OPT_IN" || item.status === "DOUBLE_OPT_IN_CHANGED_EMAIL")) ||
                        (filterProps.statusBestaetigt && item.status === "BESTAETIGT"))
                );
            }),
        ]);
    }, [data, filterProps]);

    useEffect(() => {
        if (data && filterProps) {
            filterData();
        }
    }, [data, filterProps, filterData]);

    function handleFilterChange(fieldName, newVal) {
        setVolatileFilterProps({...volatileFilterProps, [fieldName]: newVal});
    }

    async function handleDelete(index) {
        if (isDisabled()) {
            return;
        }

        confirm(
            confirmDeleteOption(
                "den Anwender",
                () => deleteAnwender(index),
                () => {
                  // Do nothing
                }
            )
        );
    }

    async function deleteAnwender(index) {
        await http
            .delete("/anwender/" + index)
            .then(() => toast.success("Gelöscht"))
            .catch((e) => {
                if (!isApiCancel(e)) {
                    if (e.response.status === 400 && e.response.data) {
                        toast.warn(e.response.data);
                    } else {
                        handleApiException(e, "Fehler beim Löschen");
                    }
                }
            });
        setTableIndex(-1);
        refetch();
    }

    function handleEdit(id) {
        if (isDisabled()) {
            return;
        }
        setTableIndex(id);
        setDlgOpen(true);
    }

    function isDisabled() {
        return !userHasAnyRole([SYSTEMADMIN]);
    }

    return (
        <div className="gridl anwenderListComponent tabsheet-content-margin">
            {dlgOpen && (
                <TableDialog
                    open={dlgOpen}
                    titelOptionen={titelOptionen}
                    anredeOptionen={anredeOptionen}
                    lokaleRollenOptionen={lokaleRollenOptionen}
                    orgaOptionen={orgaOptionen}
                    data={
                        tableIndex === -1
                            ? {
                                id: null,
                                angelegtAm: new Date(),
                                gueltigBis:
                                    globaleRollen[rollenIndex].id === GLOBALE_ROLLE_ORGANISATION
                                        ? addMonths(new Date(), 12)
                                        : new Date(3000, 0, 1),
                                status: true,
                                globaleRolle: globaleRollen[rollenIndex],
                                person: {},
                                lokaleRollen: [],
                                funktionaleRolle: [],
                            }
                            : filteredData.find((o) => o.id === tableIndex)
                    }
                    onSaveSuccessful={() => {
                        toast.success("Der Anwender wurde erfolgreich gespeichert");
                        refetch();
                        setDlgOpen(false);
                    }}
                    onClickCancel={() => setDlgOpen(false)}
                />
            )}
            <AnwenderListFilter
                filterProps={volatileFilterProps}
                onFilter={() => setFilterProps({...volatileFilterProps})}
                onFilterPropsChange={handleFilterChange}
                onFilterReset={() => setVolatileFilterProps(initialFilterProps)}
            />
            <EATable id="anwenderListe" columns={columns} initialSortColumn="organisation" data={filteredData} loading={loading}/>
            <EAButtonBar>
                <EADownloadButton
                    id={"exportAnwender"}
                    loading={loading}
                    label={"Excel-Export"}
                    disabled={!selected || selected.length === 0}
                    config={postAnwenderExportEndpointConfiguration(selected)}
                    style={{marginRight: "auto"}}
                />
                <EASelectButton
                    id="anwenderAnlegen"
                    label="Anwender anlegen"
                    options={globaleRollen.map((r) => r.title)}
                    onItemClick={(index) => {
                        setTableIndex(-1);
                        setRollenIndex(index);
                        setDlgOpen(true);
                    }}
                    color="primary"
                />{" "}
            </EAButtonBar>
        </div>
    );
}

export default AnwenderListComponent;
